// My Videos — the Internet Coin lime archive as a Windows XP Explorer window in Filmstrip view.
// Data comes from /archive/manifest.json (generated by tools/archive.ts); nothing here is hardcoded.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { WindowDropDowns } from 'components';
import { File, Help, viewMenu } from './dropDownData';
import go from 'assets/windowsIcons/290.png';
import search from 'assets/windowsIcons/299(32x32).png';
import back from 'assets/windowsIcons/back.png';
import forward from 'assets/windowsIcons/forward.png';
import up from 'assets/windowsIcons/up.png';
import folderOpen from 'assets/windowsIcons/337(32x32).png';
import menu from 'assets/windowsIcons/358(32x32).png';
import dropdown from 'assets/windowsIcons/dropdown.png';
import pullup from 'assets/windowsIcons/pullup.png';
import windows from 'assets/windowsIcons/windows.png';
import computer from 'assets/windowsIcons/676(16x16).png';
import documents from 'assets/windowsIcons/308(16x16).png';
import pictures from 'assets/windowsIcons/307(32x32).png';
import mediaPlayer from 'assets/windowsIcons/846(16x16).png';
import iePaper from 'assets/windowsIcons/ie-paper.png';
import videosIcon from 'assets/windowsIcons/myvideos16.png';

const MANIFEST_URL = '/archive/manifest.json';
const PATH = 'C:\\Documents and Settings\\fren\\My Documents\\My Videos';

function mmss(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
function mb(bytes) {
  return `${(bytes / 1048576).toFixed(1)} MB`;
}
function niceDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso + 'T12:00:00Z');
  return isNaN(d) ? iso : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}
function openApp(name) {
  window.dispatchEvent(new CustomEvent('ic:open', { detail: name }));
}

function Archive({ onClose, isFocus }) {
  const [items, setItems] = useState(null); // null = loading, [] = empty
  const [failed, setFailed] = useState(false);
  const [index, setIndex] = useState(0);
  const [view, setView] = useState('filmstrip');
  const [wantPlay, setWantPlay] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [playAll, setPlayAll] = useState(false);
  const [reload, setReload] = useState(0);
  const videoRef = useRef(null);
  const stripRef = useRef(null);
  const lastIdRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setItems(null);
    setFailed(false);
    fetch(`${MANIFEST_URL}?t=${reload}`)
      .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(m => alive && setItems(m.items || []))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [reload]);

  const count = items ? items.length : 0;
  const current = items && items[index];
  const totalBytes = useMemo(() => (items || []).reduce((s, i) => s + (i.bytes || 0), 0), [items]);

  const select = useCallback(
    (i, play) => {
      if (!count) return;
      setIndex(((i % count) + count) % count);
      if (play !== undefined) setWantPlay(play);
    },
    [count],
  );
  const next = useCallback(play => select(index + 1, play), [select, index]);
  const prev = useCallback(play => select(index - 1, play), [select, index]);

  // One persistent <video> (stable key) so Safari keeps the user-gesture activation across clips;
  // React swaps src, this effect (re)starts playback when a play intent is set.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !current || current.kind !== 'video') return;
    if (lastIdRef.current === current.id) return; // same clip: pause/play is handled by the buttons
    lastIdRef.current = current.id;
    if (wantPlay) {
      const p = v.play();
      if (p && p.catch) p.catch(() => setPlaying(false));
    }
  }, [current, wantPlay]);

  // keep the selected thumbnail in view
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const el = strip.querySelector('[data-selected="true"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [index, view]);

  // arrow keys surf the strip while this window is focused
  useEffect(() => {
    if (!isFocus) return undefined;
    function onKey(e) {
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === ' ' && current && current.kind === 'video') {
        e.preventDefault();
        togglePlay();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  function togglePlay() {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      setWantPlay(true);
      const p = v.play();
      if (p && p.catch) p.catch(() => setPlaying(false));
    } else {
      setWantPlay(false);
      setPlayAll(false);
      v.pause();
    }
  }
  function stop() {
    const v = videoRef.current;
    setWantPlay(false);
    setPlayAll(false);
    if (v) {
      v.pause();
      v.currentTime = 0;
    }
  }
  function startPlayAll() {
    setPlayAll(true);
    setWantPlay(true);
    const v = videoRef.current;
    if (v && v.paused) {
      const p = v.play();
      if (p && p.catch) p.catch(() => setPlaying(false));
    }
  }
  function fullScreen() {
    const v = videoRef.current;
    if (!v) return;
    if (v.requestFullscreen) v.requestFullscreen();
    else if (v.webkitRequestFullscreen) v.webkitRequestFullscreen();
    else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen();
  }
  function onEnded() {
    if (playAll) next(true);
    else setPlaying(false);
  }
  function onClickOptionItem(item) {
    switch (item) {
      case 'Close':
        onClose();
        break;
      case 'Filmstrip':
        setView('filmstrip');
        break;
      case 'Thumbnails':
        setView('thumbs');
        break;
      case 'Refresh':
        setReload(r => r + 1);
        break;
      case 'Play all':
        setView('filmstrip');
        startPlayAll();
        break;
      case 'Full screen':
        fullScreen();
        break;
      case 'About My Videos':
        window.alert('My Videos\nthe internet archive. every clip, lime. (real)');
        break;
      default:
    }
  }

  const dropDowns = { File, View: viewMenu(view), Help };

  return (
    <Div>
      <section className="arc__toolbar">
        <div className="arc__options">
          <WindowDropDowns items={dropDowns} onClickItem={onClickOptionItem} />
        </div>
        <img className="arc__windows-logo" src={windows} alt="windows" />
      </section>
      <section className="arc__function_bar">
        <div className="arc__function_bar__button--disable">
          <img className="arc__function_bar__icon" src={back} alt="" />
          <span className="arc__function_bar__text">Back</span>
          <div className="arc__function_bar__arrow" />
        </div>
        <div className="arc__function_bar__button--disable">
          <img className="arc__function_bar__icon" src={forward} alt="" />
          <div className="arc__function_bar__arrow" />
        </div>
        <div className="arc__function_bar__button" onClick={() => openApp('My Computer')}>
          <img className="arc__function_bar__icon--normalize" src={up} alt="" />
        </div>
        <div className="arc__function_bar__separate" />
        <div className="arc__function_bar__button">
          <img className="arc__function_bar__icon--normalize" src={search} alt="" />
          <span className="arc__function_bar__text">Search</span>
        </div>
        <div className="arc__function_bar__button">
          <img className="arc__function_bar__icon--normalize" src={folderOpen} alt="" />
          <span className="arc__function_bar__text">Folders</span>
        </div>
        <div className="arc__function_bar__separate" />
        <div
          className="arc__function_bar__button"
          title="Views"
          onClick={() => setView(v => (v === 'filmstrip' ? 'thumbs' : 'filmstrip'))}
        >
          <img className="arc__function_bar__icon--margin12" src={menu} alt="" />
          <div className="arc__function_bar__arrow" />
        </div>
      </section>
      <section className="arc__address_bar">
        <div className="arc__address_bar__title">Address</div>
        <div className="arc__address_bar__content">
          <img src={videosIcon} alt="" className="arc__address_bar__content__img" />
          <div className="arc__address_bar__content__text">{PATH}</div>
          <img src={dropdown} alt="dropdown" className="arc__address_bar__content__img" />
        </div>
        <div className="arc__address_bar__go">
          <img className="arc__address_bar__go__img" src={go} alt="go" />
          <span className="arc__address_bar__go__text">Go</span>
        </div>
      </section>

      <div className="arc__content">
        <div className="arc__left">
          <div className="arc__card">
            <div className="arc__card__header">
              <div className="arc__card__header__text">Video Tasks</div>
              <img src={pullup} alt="" className="arc__card__header__img" />
            </div>
            <div className="arc__card__content">
              <div className="arc__card__row" onClick={playAll ? stop : startPlayAll}>
                <img className="arc__card__img" src={mediaPlayer} alt="" />
                <div className="arc__card__text link">{playAll ? 'Stop playing all' : 'Play all'}</div>
              </div>
              <div className="arc__card__row" onClick={fullScreen}>
                <img className="arc__card__img" src={mediaPlayer} alt="" />
                <div className="arc__card__text link">View full screen</div>
              </div>
              <div
                className="arc__card__row"
                onClick={() => setView(v => (v === 'filmstrip' ? 'thumbs' : 'filmstrip'))}
              >
                <img className="arc__card__img" src={folderOpen} alt="" />
                <div className="arc__card__text link">
                  {view === 'filmstrip' ? 'View as thumbnails' : 'View as filmstrip'}
                </div>
              </div>
            </div>
          </div>
          <div className="arc__card">
            <div className="arc__card__header">
              <div className="arc__card__header__text">Other Places</div>
              <img src={pullup} alt="" className="arc__card__header__img" />
            </div>
            <div className="arc__card__content">
              <div className="arc__card__row">
                <img className="arc__card__img" src={documents} alt="" />
                <div className="arc__card__text link">My Documents</div>
              </div>
              <div className="arc__card__row" title="picture art — coming soon">
                <img className="arc__card__img" src={pictures} alt="" />
                <div className="arc__card__text dim">My Pictures</div>
              </div>
              <div className="arc__card__row" onClick={() => openApp('My Computer')}>
                <img className="arc__card__img" src={computer} alt="" />
                <div className="arc__card__text link">My Computer</div>
              </div>
              <div className="arc__card__row" onClick={() => openApp('Internet Explorer')}>
                <img className="arc__card__img" src={iePaper} alt="" />
                <div className="arc__card__text link">The Internet</div>
              </div>
            </div>
          </div>
          <div className="arc__card">
            <div className="arc__card__header">
              <div className="arc__card__header__text">Details</div>
              <img src={pullup} alt="" className="arc__card__header__img" />
            </div>
            <div className="arc__card__content">
              {current ? (
                <>
                  <div className="arc__card__text bold black">{current.name}</div>
                  <div className="arc__card__text">{current.kind === 'image' ? 'JPEG Image' : 'Video Clip'}</div>
                  <div className="arc__card__text">
                    Dimensions: {current.width} x {current.height}
                  </div>
                  {current.kind === 'video' && (
                    <div className="arc__card__text">Duration: {mmss(current.duration)}</div>
                  )}
                  <div className="arc__card__text">Size: {mb(current.bytes)}</div>
                  <div className="arc__card__text">Date: {niceDate(current.date)}</div>
                </>
              ) : (
                <div className="arc__card__text">{count} objects</div>
              )}
            </div>
          </div>
        </div>

        <div className="arc__right">
          {items === null && !failed && <div className="arc__msg">Loading My Videos…</div>}
          {failed && <div className="arc__msg">The archive is offline. (not real — refresh)</div>}
          {items && items.length === 0 && <div className="arc__msg">This folder is empty.</div>}
          {current && view === 'filmstrip' && (
            <>
              <div className="arc__preview">
                {current.kind === 'image' ? (
                  <img key={current.id} src={current.src} alt={current.name} className="arc__preview__media" />
                ) : (
                  <video
                    key="the-one-video"
                    ref={videoRef}
                    className="arc__preview__media"
                    src={current.src}
                    poster={current.poster}
                    controls
                    playsInline
                    preload="metadata"
                    onPlay={() => setPlaying(true)}
                    onPause={() => setPlaying(false)}
                    onEnded={onEnded}
                    onClick={e => {
                      // clicking the picture itself toggles, like the XP preview control
                      if (e.target.paused !== undefined && !e.target.controls) togglePlay();
                    }}
                  />
                )}
              </div>
              <div className="arc__filmbar">
                <button className="arc__btn" title="Previous (←)" onClick={() => prev()}>
                  ◀
                </button>
                <button className="arc__btn" title="Next (→)" onClick={() => next()}>
                  ▶
                </button>
                <span className="arc__filmbar__sep" />
                {current.kind === 'video' && (
                  <>
                    <button className="arc__btn arc__btn--wide" title="Play / Pause (space)" onClick={togglePlay}>
                      {playing ? '❚❚' : '▶ Play'}
                    </button>
                    <button className="arc__btn" title="Stop" onClick={stop}>
                      ■
                    </button>
                    <button className="arc__btn" title="Full screen" onClick={fullScreen}>
                      ⤢
                    </button>
                  </>
                )}
                <span className="arc__filmbar__sep" />
                <span className="arc__filmbar__pos">
                  {index + 1} / {count}
                  {playAll ? ' · playing all' : ''}
                </span>
              </div>
              {current.title ? (
                <div className="arc__caption" title={current.title}>
                  {current.title}
                </div>
              ) : (
                <div className="arc__caption arc__caption--empty">{current.name}</div>
              )}
              <div className="arc__strip" ref={stripRef}>
                {items.map((it, i) => (
                  <div
                    key={it.id}
                    className={`arc__thumb ${i === index ? 'arc__thumb--on' : ''}`}
                    data-selected={i === index ? 'true' : 'false'}
                    onClick={() => {
                      select(i, true);
                      const v = videoRef.current;
                      if (v && i === index && v.paused) {
                        const p = v.play();
                        if (p && p.catch) p.catch(() => setPlaying(false));
                      }
                    }}
                    onDoubleClick={() => {
                      select(i, true);
                      fullScreen();
                    }}
                    title={it.title || it.name}
                  >
                    <div className="arc__thumb__frame">
                      <img src={it.poster} alt={it.name} loading="lazy" draggable={false} />
                    </div>
                    <div className="arc__thumb__name">{it.name}</div>
                  </div>
                ))}
              </div>
            </>
          )}
          {current && view === 'thumbs' && (
            <div className="arc__grid" ref={stripRef}>
              {items.map((it, i) => (
                <div
                  key={it.id}
                  className={`arc__tile ${i === index ? 'arc__tile--on' : ''}`}
                  data-selected={i === index ? 'true' : 'false'}
                  onClick={() => select(i)}
                  onDoubleClick={() => {
                    select(i, true);
                    setView('filmstrip');
                  }}
                  title={it.title || it.name}
                >
                  <div className="arc__tile__frame">
                    <img src={it.poster} alt={it.name} loading="lazy" draggable={false} />
                    {it.kind === 'video' && <span className="arc__tile__dur">{mmss(it.duration)}</span>}
                  </div>
                  <div className="arc__tile__name">{it.name}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="arc__status">
        <div className="arc__status__cell">{items ? `${count} objects` : '…'}</div>
        <div className="arc__status__cell arc__status__cell--grow">
          {current ? current.name : ''}
        </div>
        <div className="arc__status__cell">{items ? mb(totalBytes) : ''}</div>
        <div className="arc__status__cell">
          <img src={computer} alt="" /> My Computer
        </div>
      </div>
    </Div>
  );
}

const Div = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  background: linear-gradient(to right, #edede5 0%, #ede8cd 100%);
  font-size: 11px;

  .arc__toolbar {
    position: relative;
    display: flex;
    align-items: center;
    line-height: 100%;
    height: 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.7);
    flex-shrink: 0;
  }
  .arc__options {
    height: 23px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    border-right: 1px solid rgba(0, 0, 0, 0.1);
    padding: 1px 0 1px 2px;
    flex: 1;
  }
  .arc__windows-logo {
    height: 100%;
    border-left: 1px solid white;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }
  .arc__function_bar {
    height: 36px;
    display: flex;
    align-items: center;
    font-size: 11px;
    padding: 1px 3px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    flex-shrink: 0;
  }
  .arc__function_bar__button {
    display: flex;
    height: 100%;
    align-items: center;
    border: 1px solid rgba(0, 0, 0, 0);
    border-radius: 3px;
    cursor: pointer;
    &:hover {
      border: 1px solid rgba(0, 0, 0, 0.1);
      box-shadow: inset 0 -1px 1px rgba(0, 0, 0, 0.1);
    }
    &:hover:active {
      border: 1px solid rgb(185, 185, 185);
      background-color: #dedede;
      & > * {
        transform: translate(1px, 1px);
      }
    }
  }
  .arc__function_bar__button--disable {
    filter: grayscale(1);
    opacity: 0.7;
    display: flex;
    height: 100%;
    align-items: center;
    border: 1px solid rgba(0, 0, 0, 0);
  }
  .arc__function_bar__text {
    margin-right: 4px;
  }
  .arc__function_bar__icon {
    height: 30px;
    width: 30px;
    &--normalize {
      height: 22px;
      width: 22px;
      margin: 0 4px 0 1px;
    }
    &--margin12 {
      height: 22px;
      width: 22px;
      margin: 0 1px 0 2px;
    }
  }
  .arc__function_bar__separate {
    height: 90%;
    width: 1px;
    background-color: rgba(0, 0, 0, 0.2);
    margin: 0 2px;
  }
  .arc__function_bar__arrow {
    height: 100%;
    display: flex;
    align-items: center;
    margin: 0 4px;
    &:before {
      content: '';
      display: block;
      border-width: 3px 3px 0;
      border-color: #000 transparent;
      border-style: solid;
    }
  }
  .arc__address_bar {
    flex-shrink: 0;
    border-top: 1px solid rgba(255, 255, 255, 0.7);
    height: 20px;
    display: flex;
    align-items: center;
    padding: 0 2px;
    box-shadow: inset 0 -2px 3px -1px #b0b0b0;
  }
  .arc__address_bar__title {
    line-height: 100%;
    color: rgba(0, 0, 0, 0.5);
    padding: 5px;
  }
  .arc__address_bar__content {
    border: rgba(228, 255, 122, 0.6) 1px solid;
    height: 100%;
    display: flex;
    flex: 1;
    align-items: center;
    background-color: white;
    position: relative;
    &__img {
      width: 14px;
      height: 14px;
      margin-left: 1px;
    }
    &__img:last-child {
      width: 15px;
      height: 15px;
      right: 1px;
      position: absolute;
    }
    &__text {
      white-space: nowrap;
      position: absolute;
      left: 18px;
      right: 17px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .arc__address_bar__go {
    display: flex;
    align-items: center;
    padding: 0 18px 0 5px;
    height: 100%;
    &__img {
      height: 95%;
      border: 1px solid rgba(255, 255, 255, 0.2);
      margin-right: 3px;
    }
  }

  .arc__content {
    flex: 1;
    min-height: 0;
    border: 1px solid rgba(0, 0, 0, 0.4);
    border-top-width: 0;
    display: flex;
    background: #fff;
    position: relative;
  }
  .arc__left {
    width: 180px;
    flex-shrink: 0;
    height: 100%;
    background: linear-gradient(to bottom, #e3ff74 0%, #b8d73c 100%);
    overflow: auto;
    padding: 10px;
    @media (max-width: 600px) {
      display: none;
    }
  }
  .arc__card {
    border-top-left-radius: 3px;
    border-top-right-radius: 3px;
    width: 100%;
    overflow: hidden;
    &:not(:last-child) {
      margin-bottom: 12px;
    }
  }
  .arc__card__header {
    display: flex;
    align-items: center;
    height: 23px;
    padding-left: 11px;
    padding-right: 2px;
    cursor: pointer;
    background: linear-gradient(to right, rgb(252, 255, 240) 0, rgb(252, 255, 240) 30%, rgb(238, 255, 168) 100%);
  }
  .arc__card__header__text {
    font-weight: 700;
    color: #688009;
    flex: 1;
  }
  .arc__card__header__img {
    width: 18px;
    height: 18px;
    filter: drop-shadow(1px 1px 3px rgba(0, 0, 0, 0.3));
  }
  .arc__card__content {
    padding: 5px 10px;
    background: linear-gradient(to right, rgb(238, 253, 178) 0%, rgb(235, 253, 162) 50%, rgb(238, 253, 178) 100%);
  }
  .arc__card__row {
    display: flex;
    align-items: center;
    margin-bottom: 3px;
  }
  .arc__card__img {
    width: 14px;
    height: 14px;
    margin-right: 5px;
  }
  .arc__card__text {
    font-size: 10px;
    line-height: 14px;
    color: #688009;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    &.black {
      color: #000;
    }
    &.bold {
      font-weight: bold;
    }
    &.dim {
      opacity: 0.55;
    }
    &.link {
      cursor: pointer;
    }
    &.link:hover {
      color: #d5ff2b;
      text-decoration: underline;
    }
  }

  .arc__right {
    flex: 1;
    min-width: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #fff;
    position: relative;
  }
  .arc__msg {
    margin: auto;
    color: #688009;
    font-size: 12px;
  }
  .arc__preview {
    flex: 1;
    min-height: 0;
    margin: 10px 10px 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #0d0f06;
    border: 1px solid #b0d12b;
    box-shadow: 0 0 0 1px #0d0f06, 0 0 18px rgba(204, 255, 0, 0.35);
    overflow: hidden;
  }
  .arc__preview__media {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    display: block;
    object-fit: contain;
    outline: none;
  }
  .arc__filmbar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    padding: 2px 10px;
    color: #566b00;
  }
  .arc__filmbar__sep {
    width: 1px;
    height: 16px;
    background: rgba(0, 0, 0, 0.2);
    margin: 0 5px;
  }
  .arc__filmbar__pos {
    font-size: 10px;
    color: #688009;
    white-space: nowrap;
  }
  .arc__btn {
    font: inherit;
    font-size: 11px;
    line-height: 16px;
    min-width: 24px;
    padding: 1px 5px;
    border: 1px solid #7a9a00;
    border-radius: 3px;
    background: linear-gradient(to bottom, #f6ffd0, #dcf58a);
    color: #2f3b00;
    cursor: pointer;
    &:hover {
      background: linear-gradient(to bottom, #ffffff, #e8ffa0);
      box-shadow: 0 0 4px rgba(204, 255, 0, 0.8);
    }
    &:active {
      background: #c8e64a;
    }
    &--wide {
      min-width: 56px;
    }
  }
  .arc__caption {
    flex-shrink: 0;
    text-align: center;
    padding: 0 14px 4px;
    color: #4b5a00;
    font-size: 11px;
    line-height: 14px;
    white-space: pre-line;
    max-height: 44px;
    overflow: hidden;
    text-overflow: ellipsis;
    &--empty {
      color: #9aa860;
      font-style: italic;
    }
  }
  .arc__strip {
    flex-shrink: 0;
    height: 118px;
    display: flex;
    align-items: flex-start;
    gap: 6px;
    padding: 6px 10px 8px;
    overflow-x: auto;
    overflow-y: hidden;
    border-top: 1px solid #e2ff70;
    background: linear-gradient(to bottom, #fbffe9, #f1f7d3);
    scrollbar-width: thin;
  }
  .arc__thumb {
    width: 104px;
    flex-shrink: 0;
    cursor: pointer;
    border: 1px solid transparent;
    padding: 3px;
    border-radius: 2px;
    &--on {
      border-color: #9ccc00;
      background: rgba(204, 255, 0, 0.28);
    }
    &:hover {
      background: rgba(204, 255, 0, 0.18);
    }
  }
  .arc__thumb__frame {
    height: 70px;
    background: #111;
    border: 1px solid #7a9a00;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    img {
      max-width: 100%;
      max-height: 100%;
      display: block;
    }
  }
  .arc__thumb__name {
    margin-top: 3px;
    font-size: 10px;
    line-height: 12px;
    text-align: center;
    color: #000;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .arc__thumb--on .arc__thumb__name {
    background: #ceff0b;
  }
  .arc__grid {
    flex: 1;
    min-height: 0;
    overflow: auto;
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    gap: 8px;
    padding: 12px;
  }
  .arc__tile {
    width: 132px;
    cursor: pointer;
    padding: 4px;
    border: 1px solid transparent;
    border-radius: 2px;
    &--on {
      border-color: #9ccc00;
      background: rgba(204, 255, 0, 0.28);
    }
    &:hover {
      background: rgba(204, 255, 0, 0.18);
    }
  }
  .arc__tile__frame {
    height: 96px;
    background: #111;
    border: 1px solid #7a9a00;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    position: relative;
    img {
      max-width: 100%;
      max-height: 100%;
      display: block;
    }
  }
  .arc__tile__dur {
    position: absolute;
    right: 3px;
    bottom: 2px;
    font-size: 9px;
    color: #ccff00;
    background: rgba(0, 0, 0, 0.7);
    padding: 0 3px;
    border-radius: 2px;
  }
  .arc__tile__name {
    margin-top: 3px;
    font-size: 10px;
    text-align: center;
    color: #000;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .arc__tile--on .arc__tile__name {
    background: #ceff0b;
  }
  .arc__status {
    flex-shrink: 0;
    height: 20px;
    display: flex;
    align-items: center;
    font-size: 11px;
    background: linear-gradient(to right, #edede5 0%, #ede8cd 100%);
    border-top: 1px solid rgba(0, 0, 0, 0.2);
  }
  .arc__status__cell {
    padding: 0 8px;
    border-right: 1px solid rgba(0, 0, 0, 0.15);
    box-shadow: 1px 0 rgba(255, 255, 255, 0.7);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: flex;
    align-items: center;
    gap: 4px;
    img {
      width: 14px;
      height: 14px;
    }
    &--grow {
      flex: 1;
    }
    &:last-child {
      border-right: 0;
    }
  }
`;

export default Archive;
